import React, { useContext, useState } from 'react';
//@ts-ignore
import * as styles from './related-products.module.css';
//@ts-ignore
import * as carouselStyles from './carousel.module.css';
import Carousel from './Carousel';
import Cards from './Cards';
import { RelatedProductsProps } from './index';

//@ts-ignore
import { getColor, getMultiChoice } from '../Global/DataUtils';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { getLangPrefix } from '../../global/countries';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import {
    ProductPagePreview,
    ProductPreviewData,
} from '../Global/DataUtils/productQueries';
import { ProductDescription } from '../../templates/StructuredDataContext';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import CtaLink from '../Global/CtaLink';
import RichText from '../Global/RichText';
import Button from '../Global/Button';

import Jotform from '../Jotform';

interface Props extends RelatedProductsProps {
    currentPageSlug?: string;
}

const RelatedProducts: React.FC<Props> = ({ kontentItemId, ...props }) => {
    const ctaAnchor =
        props?.cta_configuration__cta_location_anchor_point?.value;
    const pageMetadata = useContext(PageMetadataContext);
    const isMobileViewStack =
        getMultiChoice(props?.mobile_view) === 'stacked' ? true : false;
    const useCustomCtaLocation =
        getMultiChoice(props?.cta_destination) === 'custom_cta_location'
            ? true
            : false;
    const langPrefix = getLangPrefix(pageMetadata?.preferredLanguage);
    const customCtaLink =
        props?.custom_cta_location?.value[0]?.elements?.slug?.url;
    const updatedCustomCtaLink = `${langPrefix}${customCtaLink}${
        ctaAnchor ? `#${ctaAnchor}` : ''
    }`;
    const [showForm, setShowForm] = useState(false);
    const breakpoints = useBreakpoint();
    const showColoredPanel =
        getMultiChoice(props.show_colored_panel) === 'yes' &&
        getMultiChoice(props.style) === 'carousel_with_panel';

    const setCtaLocation = () => {
        if (props.cta_configuration__cta_location) {
            return props.cta_configuration__cta_location_anchor_point?.value
                ? BuildLinkHref(props.cta_configuration__cta_location) +
                      `/#${props.cta_configuration__cta_location_anchor_point.value}`
                : BuildLinkHref(props.cta_configuration__cta_location);
        }

        return undefined;
    };

    const renderCta = () => {
        const ctaType = getMultiChoice(props.cta_type);

        switch (ctaType) {
            case 'button':
                const cta = {
                    label: props.cta_configuration__cta_label?.value,
                    location: setCtaLocation(),
                    target:
                        getMultiChoice(
                            props.cta_configuration__cta_window_action
                        ) === 'new_window'
                            ? '_blank'
                            : '_self',
                };

                if (cta.location) {
                    return (
                        <CtaLink
                            href={cta.location}
                            target={cta.target}
                            color={contentPanelStyles.color || '#fff'}
                        >
                            {cta.label}
                        </CtaLink>
                    );
                }

                break;
            case 'form':
                if (props.contact_form?.value) {
                    return (
                        <Button
                            type="button"
                            visualStyle="outlineLight"
                            onClick={() => setShowForm(true)}
                        >
                            {props.cta_configuration__cta_label?.value ||
                                'Inquire'}
                        </Button>
                    );
                }
            default:
                return null;
        }
    };

    const contentPanelStyles = {
        backgroundColor:
            getColor(props.content_panel__content_panel_color) || undefined,
        color:
            getColor(props.content_panel__content_panel_font_color) ||
            undefined,
    };

    const setProducts = () => {
        switch (getMultiChoice(props.content_type)) {
            case 'explicit_product_selection':
                return props.products?.value;
            case 'product_type':
                const productFamilies =
                    props.product_type.value[0]?.elements.product_families
                        .value;
                return productFamilies
                    ?.map(
                        (productFamily) => productFamily.elements.products.value
                    )
                    ?.reduce((acc, val) => acc.concat(val), []);
            default:
                return props.product_family.value[0]?.elements.products.value;
        }
    };

    const selectedProducts = setProducts()?.filter(
        (product: ProductPagePreview) => {
            const productDescriptionElements =
                product.elements?.page_components.value.filter(
                    (component) =>
                        //@ts-ignore
                        component.__typename ===
                        'kontent_item_component___product_description'
                )[0]?.elements as ProductDescription['elements'];

            const productImage =
                getMultiChoice(
                    productDescriptionElements?.related_product_image_usage
                ) === 'use_attached_related_product_image'
                    ? productDescriptionElements?.related_product_image
                          ?.value[0]
                    : productDescriptionElements?.description_image?.value[0];

            if (
                productImage &&
                product.elements?.slug.value !== props.currentPageSlug
            ) {
                return true;
            }

            return false;
        }
    );

    const isCarousel =
        getMultiChoice(props.style) === 'carousel_with_panel' &&
        selectedProducts?.length >= 3;

    const authorStyles = {
        backgroundColor: getColor(props.background_color) || undefined,
    };

    if (selectedProducts?.length === 0) return null;

    return (
        <section
            className={styles.wrapper}
            style={authorStyles}
            id={
                props.component_anchor_name?.value
                    ? props.component_anchor_name?.value
                    : undefined
            }
            data-kontent-item-id={kontentItemId}
        >
            {breakpoints.md || !isMobileViewStack ? (
                <>
                    {isCarousel ? (
                        <Carousel
                            {...props}
                            selectedProducts={selectedProducts}
                        />
                    ) : (
                        <Cards {...props} selectedProducts={selectedProducts} />
                    )}
                    {showColoredPanel && isCarousel && (
                        <div
                            className={carouselStyles.contentPanel}
                            style={contentPanelStyles}
                        >
                            {RichText(
                                props.copy,
                                false,
                                undefined,
                                undefined,
                                'copy'
                            )}
                            {renderCta()}
                        </div>
                    )}
                </>
            ) : (
                <>
                    <Cards
                        {...props}
                        selectedProducts={selectedProducts}
                        useCustomCtaLocation={useCustomCtaLocation}
                        customCtaLink={updatedCustomCtaLink || ''}
                    />
                    {showColoredPanel && (
                        <div
                            className={carouselStyles.contentPanel}
                            style={contentPanelStyles}
                        >
                            {RichText(
                                props.copy,
                                false,
                                undefined,
                                undefined,
                                'copy'
                            )}
                            {renderCta()}
                        </div>
                    )}
                </>
            )}
            {showForm && (
                <Jotform
                    formUrl={props.contact_form?.value}
                    formStyle="popup"
                    display={true}
                    onPopUpClose={() => setShowForm(false)}
                />
            )}
        </section>
    );
};

export default RelatedProducts;
